import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { initiateLessonUpload } from '../services/lessonUpload';
import type { Order } from '../types/order';
import { useUserIdentity } from '../hooks/useUserIdentity';

interface UploadNextLessonModalProps {
  order: Order;
  nextLessonNum: number;
  onClose: () => void;
  /** Called after the upload has been initiated (still uploading in background) */
  onUploadStarted?: () => void;
  /** Reserved lesson — if set, updates this existing lesson (reserved → in_progress) instead of creating a new one */
  existingLessonId?: string;
}

export default function UploadNextLessonModal({
  order,
  nextLessonNum,
  onClose,
  onUploadStarted,
  existingLessonId,
}: UploadNextLessonModalProps) {
  const { currentUser } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [playerMessage, setPlayerMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachFirstName = (liveCoach?.name ?? order.coachName)?.split(' ')[0] || 'your coach';

  const handleFileSelect = (selected: File) => {
    if (!selected.type.startsWith('video/')) {
      setError('Please select a video file (MP4, MOV, etc.)');
      return;
    }
    setError(null);
    setFile(selected);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleSubmit = async () => {
    if (!file || !currentUser) return;
    setUploading(true);
    setError(null);
    try {
      const { cancel } = await initiateLessonUpload(
        existingLessonId
          ? {
              playerId: currentUser.uid,
              coachId: order.coachId,
              offeringId: order.offeringId,
              offeringTitle: order.offeringTitle,
              offeringType: order.type,
              totalLessons: order.totalLessons,
              priceCents: order.priceCents,
              playerMessage: playerMessage.trim() || undefined,
              file,
              existingLessonId,
            }
          : {
              playerId: currentUser.uid,
              coachId: order.coachId,
              offeringId: order.offeringId,
              offeringTitle: order.offeringTitle,
              offeringType: 'training_program',
              totalLessons: order.totalLessons,
              priceCents: order.priceCents,
              playerMessage: playerMessage.trim() || undefined,
              file,
              orderId: order.id,
              lessonNumber: nextLessonNum,
            },
        (pct) => setProgress(Math.round(pct)),
      );
      cancelRef.current = cancel;
      onUploadStarted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (uploading) {
      cancelRef.current?.();
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    >
      <div className="bg-white rounded-[20px] w-full max-w-[520px] shadow-xl overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-[18px] font-extrabold text-biscayne leading-snug">
              Upload your Lesson {nextLessonNum} video
            </div>
            <div className="text-[13px] text-db-text-2 mt-1 leading-relaxed">
              {coachFirstName} will review it and send back a personalised lesson.
            </div>
          </div>
          <button
            onClick={handleCancel}
            disabled={uploading}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-db-bg transition-colors disabled:opacity-40"
            aria-label="Close"
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="rounded-2xl border-2 border-dashed flex flex-col items-center justify-center py-10 px-6 cursor-pointer transition-colors"
            style={{
              borderColor: dragOver ? '#1A6B8A' : file ? '#22C55E' : '#D1D5DB',
              background: dragOver ? '#EAF7FD' : file ? '#F0FDF4' : '#F9FAFB',
            }}
          >
            {file ? (
              <>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-3">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#22C55E" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="text-[14px] font-semibold text-db-text-1 text-center">{file.name}</div>
                <div className="text-[12px] text-db-text-2 mt-0.5">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB · Click to change
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-db-navy-light flex items-center justify-center mb-3">
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#1A6B8A" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <div className="text-[14px] font-semibold text-db-text-1 mb-1">Drop your video here</div>
                <div className="text-[12px] text-db-text-3 mb-3">or</div>
                <span className="px-4 py-2 bg-db-accent text-white text-[13px] font-bold rounded-xl hover:bg-db-accent-hover transition-colors">
                  Browse files
                </span>
                <div className="text-[11px] text-db-text-3 mt-3">MP4, MOV · up to 30 GB</div>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
          />

          {/* Upload progress */}
          {uploading && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-[12px] text-db-text-2">
                <span>Uploading…</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-db-border overflow-hidden">
                <div
                  className="h-full rounded-full bg-db-accent transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Optional notes */}
          {!uploading && (
            <div>
              <label className="block text-[12px] font-semibold text-db-text-2 mb-1.5">
                Anything specific you want {coachFirstName} to analyze from this video?{' '}
                <span className="font-normal text-db-text-3">(optional)</span>
              </label>
              <textarea
                value={playerMessage}
                onChange={(e) => setPlayerMessage(e.target.value)}
                placeholder={`e.g. "Focus on my backhand cross-court"`}
                rows={3}
                className="w-full rounded-xl border border-db-border px-4 py-3 text-[16px] leading-[21px] text-db-text-1 placeholder-db-text-3 resize-none focus:outline-none focus:ring-2 focus:ring-db-accent focus:border-transparent input-glow"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-[12px] text-red-600 bg-red-50 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            data-testid="submit-for-lesson-btn"
            onClick={handleSubmit}
            disabled={!file || uploading}
            className="w-full py-3.5 rounded-xl text-[15px] font-bold text-white bg-db-accent hover:bg-db-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors btn-press"
          >
            {uploading ? `Uploading… ${progress}%` : `Submit for Lesson ${nextLessonNum}`}
          </button>

        </div>
      </div>
    </div>
  );
}
